import noop from 'lodash-es/noop';
import React, { Component, Fragment } from 'react';
import styled, { css } from 'styled-components';

import searchIcon from 'images/search_icon_black.svg';
import { evaluateProp } from '../utils';
import FjIcon from '../fj-icon';
// fj-styles is an untyped .js import; colors resolves as any
import { colors } from '../fj-styles';

// Also used by the fj-typeahead:
export const sharedInputDefaults = {
  height: 30,
  fontSize: 12,
  borderColor: '#231F20',
};

const keyCodes = {
  enter: 13,
  escape: 27,
};

const Icon = styled(FjIcon)`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
`;

const fixedError = css<{ alignment?: string }>`
  position: absolute;
  left: ${({ alignment }) =>
    (alignment === 'left' && '0') || (alignment === 'center' && '50%') || 'auto'};
  right: ${({ alignment }) => (alignment === 'right' && '0') || 'auto'};
  ${({ alignment }) => alignment === 'center' && 'transform: translateX(-50%)'};
  white-space: nowrap;
  padding: 0 0 10px 0;
`;

interface ValidationMessageProps {
  fontSize?: number | string;
  fixedError?: boolean;
  alignment?: string;
}

const ValidationMessage = styled.span<ValidationMessageProps>`
  color: ${colors.red.hex()};
  padding-top: 5px;
  font-size: ${(props) => evaluateProp(props.fontSize!)};
  ${(props) => props.fixedError && fixedError};
`;

interface InputStyleProps {
  isValid?: boolean;
  borderColor?: string;
  search?: boolean;
  fontSize?: number | string;
  height?: number | string;
}

export const Input = styled.input<InputStyleProps>`
  border-width: 0.5px;
  border-style: solid;
  border-color: ${(props) => (props.isValid ? props.borderColor : colors.red.hex())};
  height: ${(props) => evaluateProp(props.height!)};
  box-sizing: border-box;
  padding-left: 5px;
  width: 100%;
  font-size: ${(props) => evaluateProp(props.fontSize!)};
  color: inherit;
  background-color: ${(props) => (props.disabled ? '#f1f2f2' : 'white')};

  &::placeholder {
    color: ${(props) => (props.search ? 'transparent' : '#b4b4b4')};
  }

  // Search-specific styling:
  padding-right: ${(props) => (props.search ? '25px' : 'default')};
  border-radius: ${(props) => (props.search ? '20px' : '0')};
  background-image: ${(props) => (props.search ? `url(${searchIcon})` : 'none')};
  background-repeat: no-repeat;
  background-size: 12px 12px;
  background-position: 6px 50%;
  &:focus {
    background-image: none;
  }
  &:not(:placeholder-shown) {
    background-image: none;
  }
`;

// ------------------------------------------------------------------------------------------------

interface FjInputProps {
  value?: string;
  type: string;
  id: string | null;
  validator: (value: string) => boolean;
  validateImmediate: boolean;
  placeholder: string;
  onChange: (value: string) => void;
  onCancel: (value: string) => void;
  onSubmit: (value: string) => void;
  onFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled: boolean;
  autoFocus: boolean;
  className: string;
  search: boolean;
  errorAlign: string;
  fixedError: boolean;
  height: number | string;
  fontSize: number | string;
  borderColor: string;
  autoComplete?: string;
  getValidationMessage?: string | ((value: string) => string);
}

interface FjInputState {
  value: string;
  isValid: boolean;
}

class FjInput extends Component<FjInputProps, FjInputState> {
  static defaultProps = {
    ...sharedInputDefaults,
    type: 'text',
    id: null,
    validator: () => true,
    validateImmediate: false,
    placeholder: '',
    onChange: noop,
    onCancel: noop,
    onSubmit: noop,
    onFocus: noop,
    onBlur: noop,
    disabled: false,
    autoFocus: false,
    className: '',
    search: false,
    errorAlign: 'left',
    fixedError: false,
  };

  state: FjInputState = {
    value: this.props.value || '',
    isValid: true,
  };

  inputRef = React.createRef<HTMLInputElement>();

  // istanbul ignore next

  UNSAFE_componentWillReceiveProps(nextProps: FjInputProps): void {
    if (typeof nextProps.value === 'undefined') {
      return;
    }
    if (nextProps.value !== this.state.value) {
      this.setState({
        value: nextProps.value,
        isValid: nextProps.validator(nextProps.value),
      });
    }
  }

  onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.keyCode === keyCodes.enter) {
      this.setState(
        (prevState) => ({ isValid: this.props.validator(prevState.value) }),
        () => {
          if (this.state.isValid) {
            this.props.onSubmit(this.state.value);
          }
        },
      );
    } else if (e.keyCode === keyCodes.escape) {
      this.props.onCancel(this.state.value);
    }
  };

  updateValue = (ev: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = ev.target;
    const isValid = this.props.validator(value);

    this.setState(
      {
        value,
        isValid,
      },
      () => this.props.onChange(this.state.value),
    );
  };

  handleClear = (): void => {
    this.setState(
      {
        value: '',
        isValid: true,
      },
      () => {
        this.inputRef.current!.focus();
        this.props.onChange(this.state.value);
      },
    );
  };

  isFocused = (): boolean => this.inputRef.current === document.activeElement;

  isValid = (): boolean => {
    if (this.props.validateImmediate) {
      return this.state.isValid;
    }
    return this.isFocused() ? true : this.state.isValid;
  };

  // Can be called by consuming code via ref
  resetValidation = (): void => this.setState({ isValid: true });

  forceValidation = (): void =>
    this.setState(({ value }) => ({
      isValid: this.props.validator(value),
    }));

  handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
    this.forceValidation();
    this.props.onBlur(e);
  };

  handleFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
    this.forceUpdate();
    this.props.onFocus(e);
  };

  renderValidationMessage(): React.ReactNode {
    const errorMsg = this.props.getValidationMessage;
    if (this.isValid() || !errorMsg) {
      return null;
    }

    const msg = typeof errorMsg === 'function' ? errorMsg(this.state.value) : errorMsg;
    // Messages with newlines get mapped to spans separated by line breaks

    return msg ? (
      <ValidationMessage
        fontSize={this.props.fontSize}
        alignment={this.props.errorAlign}
        fixedError={this.props.fixedError}
      >
        {msg.split('\n').map((line, index) => (
          <React.Fragment key={line.toLowerCase().replace(/ /g, '-')}>
            {index !== 0 && <br />}
            <span>{line}</span>
          </React.Fragment>
        ))}
      </ValidationMessage>
    ) : null;
  }

  renderCloseButton(): React.ReactNode {
    if (!this.props.search) {
      return null;
    }

    const xIcon = (
      <Fragment>
        <line x1="1" y1="8" x2="8" y2="1" stroke={this.props.borderColor} strokeWidth="1" />
        <line x1="1" y1="1" x2="8" y2="8" stroke={this.props.borderColor} strokeWidth="1" />
      </Fragment>
    );

    return (
      <Icon
        inline
        icon={xIcon}
        viewBox="0 0 10 10"
        height={10}
        width={10}
        alt="Clear search"
        onClick={this.handleClear}
      />
    );
  }

  renderInput(): React.ReactElement {
    return (
      <Input
        ref={this.inputRef}
        className="FjInput"
        type={this.props.type}
        id={this.props.id ?? undefined}
        key={this.props.id}
        placeholder={this.props.search ? ' ' : this.props.placeholder}
        // ^ Whitespace string required for search CSS to work
        height={this.props.height}
        isValid={this.isValid()}
        value={this.state.value}
        onChange={this.updateValue}
        onKeyDown={this.onKeyDown}
        fontSize={this.props.fontSize}
        search={this.props.search}
        borderColor={this.props.borderColor}
        disabled={this.props.disabled}
        onBlur={this.handleBlur}
        onFocus={this.handleFocus}
        autoFocus={this.props.autoFocus}
        autoComplete={this.props.autoComplete}
      />
    );
  }

  render(): React.ReactNode {
    return (
      <div className={this.props.className} style={{ position: 'relative' }}>
        <div style={{ position: 'relative' }}>
          {this.renderInput()}
          {this.renderCloseButton()}
        </div>
        {this.renderValidationMessage()}
      </div>
    );
  }
}

export default FjInput;
